import { addDays, monthGrid, startOfWeek, type WeekStartsOn } from '@/lib/eventCalendar'

/** A week row's height in rem — fixed (`h-12`) so the calendar can size its viewport without measuring. */
export const WEEK_ROW_REM = 3

/**
 * The week rows to render for `month`. On the current month they're the union
 * of the month grid and the two weeks from today (which can run into next month).
 */
export function calendarWeeks(month: string, currentMonth: string, today: string, weekStartsOn: WeekStartsOn) {
  const monthDays = monthGrid(month, weekStartsOn)
  const first = startOfWeek(today, weekStartsOn)
  const twoWeeks = new Set([first, addDays(first, 7)])
  const starts = new Set<string>()
  for (let i = 0; i < monthDays.length; i += 7) starts.add(monthDays[i])
  const inMonth = new Set(starts)
  if (month === currentMonth) twoWeeks.forEach(w => starts.add(w))
  return [...starts].sort().map(start => ({
    days: Array.from({ length: 7 }, (_, i) => addDays(start, i)),
    inMonth: inMonth.has(start),
    inTwoWeeks: twoWeeks.has(start),
  }))
}

/**
 * The rows a page shows, as the index of the first and how many: the whole
 * month, or the two weeks from today. The month's rows come first, so either
 * window is a contiguous run.
 */
export function weekWindow(month: string, today: string, weekStartsOn: WeekStartsOn, expanded: boolean) {
  const monthRows = monthGrid(month, weekStartsOn).length / 7
  if (expanded) return { top: 0, rows: monthRows }
  const first = startOfWeek(today, weekStartsOn)
  return { top: Math.round((Date.parse(first) - Date.parse(monthGrid(month, weekStartsOn)[0])) / (7 * 86_400_000)), rows: 2 }
}
